import {
    IModularFamily,
    IPurchaseAttributes,
    ISelectedAttributes,
    IUserBenefits
} from '../../../redux/slices/ModularEnrolmentSlice/types';
import { IAlacarte } from '../../../redux/slices/PolicyListSlice/types';
import { capitalizeFirstLetter, formatAmount } from '../../../utils/common';
import { sortFamilyMembers } from '../ModularAddDependents/utils';
import {
    IBenefitPerLifeCurrentProceedStatus,
    IBenefitPerLifeMemberConfig,
    IBenefitPerLifeMemberGenerate,
    IBenefitPerLifeMemberValue,
    IBenefitPerLifeUpdatedSelections
} from './types';

export const getMaxEligibleFamilyMembers = (plan: IAlacarte, family: IModularFamily[]): IModularFamily[] => {
    const familyDict = family.reduce((acc, m) => ({ ...acc, [m.id]: m }), {});
    const maxEligibleMembers: Record<string, IModularFamily> = {};
    plan.eligibleUserIds?.concat(plan.subCategories.flatMap((s) => s.eligibleUserIds ?? []))?.forEach((id) => {
        if (!familyDict[id] || maxEligibleMembers[id]) return;
        maxEligibleMembers[id] = familyDict[id];
    });
    return sortFamilyMembers(Object.values(maxEligibleMembers)) as IModularFamily[];
};

export const getDefaultSelectedUserBenefits = (
    plan: IAlacarte,
    userBenefits: IUserBenefits, // contains all benefits for policy
    family?: IModularFamily[] // optional, used for per life member benefits
): Record<string, IBenefitPerLifeMemberValue> => {
    const selectedBenefits: Record<string, IBenefitPerLifeMemberValue> = {};
    const plans = plan.subCategories.length ? plan.subCategories : [plan];
    plans.forEach((p) => {
        userBenefits[p.id]?.forEach((userId) => {
            selectedBenefits[userId] = {
                checked: true,
                value: p.id
            };
        });
    });
    if (plan.isSelfMandatory) {
        const self = family?.find((f) => f.relationship.toLowerCase() === 'self');
        if (self && !selectedBenefits[self.id]) {
            selectedBenefits[self.id] = {
                checked: true,
                value: plans.length > 1 ? '' : plans[0].id
            };
        }
    }
    // contains only current plan user benefits
    return selectedBenefits;
};

export const getDefaultSelectedPurchaseAttributes = (
    selectedItem: string,
    purchaseAttributes: IPurchaseAttributes,
    selectedAttributes: ISelectedAttributes,
    family?: IModularFamily[]
): Record<string, string> => {
    if (!selectedItem) return {};
    const selfId = family?.find((m) => m.relationship.toLowerCase() === 'self')?.id || 'user';
    const prefill = selectedAttributes?.[selectedItem]?.[selfId] || {};
    const initialDetails = {};
    Object.keys(purchaseAttributes).forEach((fieldKey) => {
        initialDetails[fieldKey] = prefill?.[fieldKey] ?? '';
    });
    return initialDetails;
};

// to generate the config for the dropdown options in the modal
export const generatePerLifeMemberConfig = (
    plan: IAlacarte,
    family: IModularFamily[]
): IBenefitPerLifeMemberGenerate => {
    const covered = {};
    const plans = plan.subCategories.length ? plan.subCategories : [plan];
    plans.forEach((s) => {
        s.eligibleUserIds?.forEach((id) => {
            if (!covered[id]) covered[id] = { dropdownOptions: [] };
            covered[id].dropdownOptions.push({
                name: `${s.name} - ${formatAmount(s.price)}`,
                value: s.id,
                price: s.price
            });
        });
    });
    const members: IBenefitPerLifeMemberConfig[] = [];
    const unc = new Set<string>();
    family.forEach((f) => {
        if (covered[f.id]) {
            covered[f.id].data = f;
            covered[f.id].disabledCheckbox = plan.isSelfMandatory && f.relationship.toLowerCase() === 'self';
            members.push(covered[f.id]);
        } else {
            unc.add(capitalizeFirstLetter(f.relationship));
        }
    });
    const uncArray = Array.from(unc);
    const uncoveredText = uncArray.length
        ? uncArray.length > 1
            ? `${uncArray.slice(0, -1).join(', ')} and ${uncArray.slice(-1)} are`
            : `${uncArray[0]} is`
        : undefined;

    return {
        members,
        uncoveredText
    };
};

export const getBenefitPerLifeCurrentProceedStatus = (
    values: Record<string, IBenefitPerLifeMemberValue>
): IBenefitPerLifeCurrentProceedStatus => {
    const arr = Object.values(values);
    const hasSelectedBenefits = arr.some((v) => v.checked);
    let hasNotSelectedValues = false;
    if (!hasSelectedBenefits) return { hasSelectedBenefits, hasNotSelectedValues };
    arr.forEach(({ checked, value }) => {
        hasNotSelectedValues = hasNotSelectedValues || (checked && !value);
    });
    return { hasSelectedBenefits, hasNotSelectedValues };
};

export const calculatePerLifeTotalCost = (
    plan: IAlacarte,
    values: Record<string, IBenefitPerLifeMemberValue>
): number => {
    const plans = plan.subCategories.length ? plan.subCategories : [plan];
    return Object.values(values).reduce((total, { checked, value }) => {
        const selectedPlan = plans.find((p) => p.id === value);
        if (selectedPlan && checked) {
            return total + selectedPlan.price;
        }
        return total;
    }, 0);
};

export const getPerLifeUpdatedSelections = (
    planIds: string[],
    values: Record<string, IBenefitPerLifeMemberValue>,
    selectedBenefits: string[],
    selectedUserBenefits: IUserBenefits,
    deletion = false
): IBenefitPerLifeUpdatedSelections => {
    const updatedUserBenefits = structuredClone(selectedUserBenefits);
    const updatedBenefits = selectedBenefits.filter((b) => !planIds.includes(b));
    planIds.forEach((p) => delete updatedUserBenefits[p]);
    if (!deletion) {
        Object.entries(values).forEach(([userId, { checked, value }]) => {
            if (checked && value) {
                updatedUserBenefits[value] = [...(updatedUserBenefits[value] ?? []), userId];
                if (!updatedBenefits.includes(value)) {
                    updatedBenefits.push(value);
                }
            }
        });
    }
    return { updatedUserBenefits, updatedBenefits };
};

export const handlePerLifeFormUpdations = (
    selectedAttributes: ISelectedAttributes,
    selectedMemberDetails: IBenefitPerLifeMemberConfig[],
    purchaseAttributes: IPurchaseAttributes
): Record<string, Record<string, string>> => {
    const initial: Record<string, Record<string, string>> = {};
    selectedMemberDetails.forEach(({ data }) => {
        initial[data.id] = {};
        Object.keys(purchaseAttributes).forEach((fieldKey) => {
            let prefill = '';
            Object.keys(selectedAttributes).forEach((planId) => {
                if (
                    selectedAttributes[planId] &&
                    selectedAttributes[planId][data.id] &&
                    selectedAttributes[planId][data.id][fieldKey] !== undefined
                ) {
                    prefill = selectedAttributes[planId][data.id][fieldKey];
                }
            });
            initial[data.id][fieldKey] = prefill;
        });
    });
    return initial;
};

export const validatePerLifeFormSelection = (
    step: number,
    selectedMembers: string[],
    isProceedEnabled: boolean,
    purchaseAttributes: IPurchaseAttributes,
    formValues: Record<string, Record<string, string>>,
    showError: boolean
): boolean => {
    if (showError) return false;
    switch (step) {
    case 1:
        return selectedMembers.length > 0 && isProceedEnabled;
    case 2:
        return selectedMembers.every((userId) =>
            Object.keys(purchaseAttributes).every((fieldKey) => {
                const attribute = purchaseAttributes[fieldKey];
                if (attribute?.required) {
                    return (
                        !!formValues[userId] &&
                            typeof formValues[userId][fieldKey] === 'string' &&
                            formValues[userId][fieldKey].trim() !== ''
                    );
                }
                return true;
            })
        );
    default:
        return true;
    }
};

export const validatePerFamilyFormSelection = (
    purchaseAttributes: IPurchaseAttributes,
    formValues: Record<string, string>,
    showError: boolean
): boolean => {
    if (showError) return false;
    const isValid = Object.keys(purchaseAttributes).every((fieldKey) => {
        const attribute = purchaseAttributes?.[fieldKey];
        if (attribute?.required) {
            const value = formValues?.[fieldKey];
            return typeof value === 'string' && value.trim() !== '';
        }
        return true;
    });
    return isValid;
};

export const getUpdatedAttributesForPerLife = (
    values: Record<string, IBenefitPerLifeMemberValue>,
    formValues: Record<string, Record<string, string>>,
    selectedMembers: string[],
    recentAttributes: Record<string, Record<string, string>> = {}
): ISelectedAttributes => {
    const purchaseAttributeUsers = Object.keys(formValues) ?? [];
    const purchasePayload = {};
    purchaseAttributeUsers.forEach((userId) => {
        if (!selectedMembers.includes(userId)) return;
        const value = formValues[userId];
        const selectedBenefit = values[userId].value;
        if (!selectedBenefit) return;
        if (value) {
            purchasePayload[selectedBenefit] = {
                ...purchasePayload[selectedBenefit],
                [userId]: value
            };
        }
    });

    const updatedAttributes = {
        ...recentAttributes,
        ...purchasePayload
    };
    return updatedAttributes;
};
